"use client";

import { useCallback, useEffect, useState } from "react";
import { getFacetedSearchResults, Search, SearchResult } from "../search";
import { encodeSearch } from "../utils/encode-search";

export function useInfiniteScroll({
  search,
  initialResult,
}: {
  search: Search;
  initialResult: SearchResult;
}) {
  const searchId = encodeSearch(search);

  const [state, setState] = useState<
    SearchResult & {
      isLoading: boolean;
    }
  >({
    ...initialResult,
    isLoading: false,
  });

  const { nbHits, results, isLoading, facets } = state;

  const isLastPage = nbHits <= results.length;

  const runSearch = useCallback(
    async ({ nextOffset, append }: { nextOffset: number; append: boolean }) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const {
          results: hits,
          nbHits,
          facets,
        } = await getFacetedSearchResults({
          ...search,
          offset: nextOffset,
        });
        setState((prev) => ({
          ...prev,
          results: append ? [...prev.results, ...hits] : hits,
          nbHits: nbHits ?? nextOffset + hits.length,
          facets,
          isLoading: false,
        }));
      } catch {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [search],
  );

  const loadMore = useCallback(async () => {
    if (isLoading || isLastPage) return;
    await runSearch({ nextOffset: results.length, append: true });
  }, [isLastPage, isLoading, results.length, runSearch]);

  useEffect(() => {
    const shouldUseInitial =
      initialResult.results.length > 0 && initialResult.searchId === searchId;

    if (shouldUseInitial) {
      setState((prev) => ({ ...prev, ...initialResult, isLoading: false }));
      return;
    }

    runSearch({ nextOffset: 0, append: false });
  }, [initialResult, searchId, runSearch]);

  return {
    results,
    isLastPage,
    loadMore,
    isLoading,
    nbHits,
    facets,
    searchId: state.searchId,
  };
}
